import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../style/CustomerProfile.css";

function JointProfile() {
  const location = useLocation();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [lookupValue, setLookupValue] = useState("");
  const [accountInfo, setAccountInfo] = useState(null);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const endPoint = process.env.REACT_APP_API_URL;

  const getCookie = (name) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i].trim();
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return null;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    let sessionId = params.get("s") || params.get("sessionId");
    const dataParam = params.get("data");
    
    if (sessionId) {
      sessionStorage.setItem("sessionId", sessionId);
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
    
    if (!sessionId) {
      sessionId = getCookie("sessionId");
    }
    
    if (!sessionId) {
      sessionId = sessionStorage.getItem("sessionId");
    }
    
    if (sessionId) {
      fetchUserData(sessionId);
    } else if (dataParam) {
      try {
        const parsedData = JSON.parse(decodeURIComponent(dataParam));
        setUserData(parsedData);
        setLoading(false);
      } catch (err) {
        setError("Failed to parse user data");
        setLoading(false);
      }
    } else {
      setError("No session found. Please try logging in again.");
      setTimeout(() => navigate("/"), 3000);
      setLoading(false);
    }
  }, [location.search]);

  const fetchUserData = async (sessionId) => {
    try {
      setLoading(true);
      setError("");
      
      const url = `${endPoint}getUserData?sessionId=${sessionId}`;
      
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUserData(data);
      } else if (response.status === 404 || response.status === 410) {
        setError("Session not found or expired. Please try again.");
        sessionStorage.removeItem("sessionId");
        setTimeout(() => navigate("/"), 3000);
      } else {
        setError("Failed to load user data");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCheck = async () => {
    setError("");
    setAccountInfo(null);
    setSuccessMsg("");

    const cleaned = lookupValue.trim();

    if (!cleaned) {
      setError("Please enter customer number or account number");
      return;
    }

    let payload = {
      name: userData.name || "",
      gender: userData.gender || "",
      phoneNumber: userData.phone_number || "",
      psut: userData.sub || "",
      birthdate: userData.birthdate || "",
      email: userData.email || "",
      region: userData.address?.region || "",
      zone: userData.address?.zone || "",
      woreda: userData.address?.woreda || "",
      picture: userData.picture || "",
    };

    if (/^\d{13}$/.test(cleaned)) {
      payload.accountNumber = cleaned;
    } else if (/^\d{7}$/.test(cleaned)) {
      payload.customerNumber = cleaned;
    } else {
      setError("Please enter a valid 13-digit account number or 7-digit customer number");
      return;
    }

    try {
      const res = await fetch(`${endPoint}lookupJoint`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.status === 404) {
        setError("Account or Customer not found");
        return;
      }

      if (!res.ok) {
        setError("Server error");
        return;
      }

      const data = await res.json();
      setAccountInfo(data);
    } catch (err) {
      setError("Server error");
    }
  };

  const handleConfirm = async () => {
    if (!accountInfo) return;

    setError("");
    setSuccessMsg("");
    try {
      const picture = userData.picture || "";
      const picture2 = picture
        ? picture.replace(
            "https://obfayda.oromiabank.com",
            "https://obfayda.ob.com"
          )
        : "";

      const payload = {
        psut: userData.sub,
        accountNumber: accountInfo.accountNumber,
        customerNumber: accountInfo.originalCustomerNumber,
        name: userData.name || "",
        gender: userData.gender || "",
        nationality: userData.nationality || "",
        phoneNumber: userData.phone_number || "",
        birthdate: userData.birthdate || "",
        email: userData.email || "",
        region: userData.address?.region || "",
        zone: userData.address?.zone || "",
        woreda: userData.address?.woreda || "",
        picture: picture,
        picture2: picture2,
        branch: accountInfo.branch,
      };

      const res = await fetch(`${endPoint}confirmJointOb`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSuccessMsg("Your NID info is successfully linked to your account. Thank you!");
        setTimeout(() => navigate("/"), 5000);
      } else {
        setError("Server error while sending data");
      }
    } catch {
      setError("Server error while sending data");
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-card">
          <div className="loading-spinner">Loading your profile data...</div>
        </div>
      </div>
    );
  }

  if (error && !userData) {
    return (
      <div className="profile-container">
        <div className="profile-card">
          <div className="error-message" style={{ textAlign: "center", padding: "20px" }}>
            <h3>Error</h3>
            <p>{error}</p>
            <button
              onClick={() => navigate("/")}
              style={{ marginTop: "10px", padding: "8px 16px", cursor: "pointer" }}
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="profile-container">
        <div className="profile-card">
          <p>No user data available. Please try again.</p>
          <button onClick={() => navigate("/")}>Go Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-card">
        <div className="profile-header">
          {userData.picture ? (
            <div className="profile-picture">
              <img src={userData.picture} alt="User profile" />
            </div>
          ) : (
            <div className="profile-picture placeholder">No Image</div>
          )}
          <h2>{userData.name}</h2>
          <p className="user-id">PSTU: {userData.sub}</p>
        </div>

        {/* Using profile-details-grid from CustomerProfile.css */}
        <div className="profile-details-grid">
          <div className="detail-item"><strong>Gender:</strong> {userData.gender}</div>
          <div className="detail-item"><strong>Birthdate:</strong> {userData.birthdate}</div>
          <div className="detail-item"><strong>Phone:</strong> {userData.phone_number}</div>
          <div className="detail-item"><strong>Nationality:</strong> {userData.nationality}</div>
        </div>

        <div className="verification">
          <p>
            Success: Your identity has been verified. To link with your profile, provide one of your Oromia Bank account numbers below.
          </p>
        </div>

        <div className="lookup-section">
          <input
            type="text"
            placeholder="Enter 13-digit Joint Account Number or Customer Number"
            value={lookupValue}
            maxLength={13}
            onChange={(e) => setLookupValue(e.target.value.replace(/\s+/g, ""))}
          />
          <button className="check-btn" onClick={handleCheck}>Check</button>
        </div>

        {error && <p className="error-message">{error}</p>}
        {successMsg && <p className="success-message">{successMsg}</p>}

        {accountInfo && !successMsg && (
          <div className="bank-info-box">
            <h3>Information from Oromia Bank</h3>
            <div className="bank-data-grid">
              <p><strong>Name:</strong> {accountInfo.customerName}</p>
              <p><strong>Account Number:</strong> {accountInfo.accountNumber}</p>
              <p><strong>Customer Number:</strong> {accountInfo.customerNumber}</p>
              <p><strong>Branch Code:</strong> {accountInfo.branch}</p>
              <p><strong>Branch Name:</strong> {accountInfo.branchName}</p>
            </div>

            <div className="button-group">
              <button className="btn confirm" onClick={handleConfirm}>Confirm</button>
              <button className="btn cancel" onClick={() => navigate("/")}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default JointProfile;